import type { StateCreator } from 'zustand';
import type { StoreType } from '@/types/store';

/**
 * Recompute middleware (Stage 10 remediation C8).
 *
 * Schedules a debounced `state.compute()` whenever a set-call produces
 * a new `studies` array reference. Every study-mutating action returns
 * a fresh array via `.map(...)`, so referential comparison is sufficient
 * and faster than inspecting the partial shape. Non-study mutations
 * (`setActiveScreen`, `setSection`, `pushToast`, etc.) leave `studies`
 * referentially unchanged and therefore correctly skip recompute.
 */
export function createRecomputeMiddleware<T extends StoreType>(
  storeInitializer: StateCreator<T, [], []>,
): StateCreator<T, [], []> {
  return (set, get, api) => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const wrappedSet: typeof set = (partial, replace) => {
      const prevStudies = (get() as unknown as StoreType).studies;
      (set as (partial: unknown, replace?: boolean) => void)(partial, replace);
      const nextStudies = (get() as unknown as StoreType).studies;
      if (prevStudies !== nextStudies) {
        schedule();
      }
    };

    function schedule() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        const state = get() as unknown as StoreType;
        if (typeof state.compute === 'function') {
          state.compute();
        }
      }, 150);
    }

    return storeInitializer(wrappedSet as typeof set, get, api);
  };
}
